import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
  PlusCircleIcon,
  ClockIcon,
  FolderIcon,
  Squares2X2Icon,
  ReceiptRefundIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { BoltIcon, RocketLaunchIcon } from '@heroicons/react/24/solid';
import { toast } from 'sonner';
import { apiClient } from '../../lib/axios';
import { useAuthStore } from '../../store/useAuthStore';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import PlansModal from '../dashboard/PlansModal';
import { ApiPlan, PlanCard, buildPlanCards } from '../../lib/plans';
import { submitPayuForm } from '../../lib/payu';
import { openRazorpayCheckout } from '../../lib/razorpay';

interface DashboardStats {
  credits_left: number;
  recent_searches_count: number;
  recent_searches: {
    id: string;
    query: string;
    type: string;
    confidence: number;
    status: string;
  }[];
}

interface SearchTask {
  id: string;
  type: string;
  company: string;
  status: string;
  credits_used: number;
  created_at: string;
  depth?: number;
}

interface Payment {
  id: string;
  plan_name: string;
  credits: number;
  remaining_credits: number;
  amount: string;
  status: string;
  payment_method: string | null;
  expires_at: string | null;
  created_at: string;
}

const DEPTH_LABELS: Record<number, string> = {
  1: 'Normal',
  2: '×2',
  3: '×3',
  4: '×4',
  5: '×5',
};

export default function Billing() {
  useDocumentTitle('Billing & Usage', 'Monitor your Localcontact credit usage and manage your subscription plan.');

  const { user } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [tasks, setTasks] = useState<SearchTask[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [cards, setCards] = useState<PlanCard[]>([]);
  const [customPlans, setCustomPlans] = useState<ApiPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPlansOpen, setIsPlansOpen] = useState(false);
  const [hoveredPlan, setHoveredPlan] = useState<number | null>(null);
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const startCheckout = async (card: PlanCard) => {
    setBuyingId(card.id);
    try {
      const res = await apiClient.post('/payment/create-order', { plan_id: card.id });
      const data = res.data;

      if (data.gateway === 'razorpay') {
        const payment = await openRazorpayCheckout(data);
        await apiClient.post('/payment/razorpay/verify', payment);
        toast.success('Payment successful! Your credits have been added.');
        loadBillingData();
        setBuyingId(null);
        return;
      }

      // PayU hosted checkout
      toast.message('Redirecting to secure checkout…');
      submitPayuForm(data.action_url, data.params);
    } catch (e: any) {
      if (e?.message === 'Payment cancelled') { setBuyingId(null); return; }
      toast.error(e?.response?.data?.error || e?.message || 'Could not start checkout.');
      setBuyingId(null);
    }
  };

  const loadBillingData = () => {
    Promise.all([
      apiClient.get('/user/dashboard-stats'),
      apiClient.get('/search/history'),
      apiClient.get('/user/payments'),
      apiClient.get('/plans'),
      apiClient.get('/user/custom-plans'),
    ]).then(([statsRes, historyRes, paymentsRes, plansRes, customRes]) => {
      setStats(statsRes.data);
      setTasks((historyRes.data.tasks as SearchTask[]).slice(0, 20));
      setPayments(paymentsRes.data);
      setCards(buildPlanCards(plansRes.data as ApiPlan[]));
      setCustomPlans(customRes.data as ApiPlan[]);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle the PayU hosted-checkout return (?payment=success|failed|pending)
  useEffect(() => {
    const result = searchParams.get('payment');
    if (!result) return;
    if (result === 'success') {
      toast.success('Payment successful! Your credits have been added.');
      loadBillingData();
    } else if (result === 'failed') {
      toast.error('Payment failed or was cancelled. You were not charged.');
    } else if (result === 'pending') {
      toast.message('Payment is pending confirmation. Credits will appear once confirmed.');
    }
    searchParams.delete('payment');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalCreditsUsed = tasks.reduce((sum, t) => sum + (t.credits_used || 0), 0);
  const currentBalance = stats?.credits_left ?? user?.credits ?? 0;

  const creditsByType = tasks.reduce<Record<string, number>>((acc, t) => {
    acc[t.type] = (acc[t.type] || 0) + (t.credits_used || 0);
    return acc;
  }, {});

  const formatDate = (s: string) =>
    new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return d.toISOString().split('T')[0];
  });

  const usageByDay = last7Days.map(date => ({
    date,
    credits: tasks
      .filter(t => t.created_at.startsWith(date))
      .reduce((sum, t) => sum + (t.credits_used || 0), 0),
  }));

  const maxDayCredits = Math.max(...usageByDay.map(d => d.credits), 1);

  return (
    <div className="h-full flex flex-col bg-slate-50/50">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in { animation: fadeIn 0.2s ease-out both; }
      `}</style>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-6 sm:py-8 flex flex-col gap-8">

          {/* Page heading */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Billing & Usage</h1>
              <p className="text-sm text-slate-500 mt-1">Monitor your credit usage and manage your plan.</p>
            </div>
            <button
              onClick={() => setIsPlansOpen(true)}
              className="flex items-center gap-2 bg-brand-500 text-slate-900 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors [box-shadow:0_4px_16px_rgba(0,84,214,0.35)] self-start sm:self-auto"
            >
              <PlusCircleIcon className="w-4 h-4" /> Get Credits
            </button>
          </div>

          {/* Balance + usage — rounded cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 fade-in">
            <div className="rounded-2xl p-6 bg-brand-600 border border-brand-700 shadow-sm">
              <p className="text-[9px] font-bold text-white uppercase tracking-wider mb-2">Current Balance</p>
              <div className="flex items-end gap-2">
                <BoltIcon className="w-6 h-6 text-white mb-0.5" />
                <p className="text-[40px] font-extrabold text-white leading-none tracking-tight">
                  {loading ? '—' : currentBalance.toLocaleString()}
                </p>
              </div>
              <p className="text-[11px] text-white font-medium mt-2">Available credits</p>
            </div>
            <div className="rounded-2xl p-6 bg-white border border-slate-200 shadow-sm">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">Total Used</p>
              <p className="text-[36px] font-extrabold text-slate-900 leading-none tracking-tight mt-1">
                {loading ? '—' : totalCreditsUsed.toLocaleString()}
              </p>
              <p className="text-[11px] text-slate-400 font-medium mt-2">Across {tasks.length} searches</p>
            </div>
            <div className="rounded-2xl p-6 bg-white border border-slate-200 shadow-sm">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">By Type</p>
              <div className="flex flex-col gap-2 mt-1">
                {Object.entries(creditsByType).map(([type, credits]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {type === 'bulk'
                        ? <FolderIcon className="w-3.5 h-3.5 text-slate-500" />
                        : <Squares2X2Icon className="w-3.5 h-3.5 text-slate-700" />
                      }
                      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-700">{type}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <BoltIcon className="w-3 h-3 text-slate-700" />
                      <span className="text-[13px] font-extrabold text-slate-800">{credits.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
                {Object.keys(creditsByType).length === 0 && (
                  <p className="text-[12px] text-slate-400">No usage yet.</p>
                )}
              </div>
            </div>
          </div>

          {/* 7-day usage chart */}
          <div className="flex flex-col gap-3 fade-in">
            <div className="flex items-center gap-2">
              <ChartBarIcon className="w-4 h-4 text-slate-400" />
              <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wide">Credits Used, Last 7 Days</span>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
              <div className="flex items-end gap-3 h-28">
                {usageByDay.map((d, i) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" style={{ animationDelay: `${i * 40}ms` }}>
                    <div className="w-full flex flex-col justify-end h-24 relative">
                      {d.credits > 0 && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                          <div className="bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded-lg whitespace-nowrap">
                            {d.credits} credit{d.credits !== 1 ? 's' : ''}
                          </div>
                        </div>
                      )}
                      <div
                        className="w-full bg-brand-500 rounded-t group-hover:bg-brand-600 transition-colors duration-150"
                        style={{ height: `${Math.round((d.credits / maxDayCredits) * 100)}%`, minHeight: d.credits > 0 ? '4px' : '0' }}
                      />
                    </div>
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">
                      {new Date(d.date + 'T00:00:00').toLocaleDateString('en', { weekday: 'short' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Usage */}
          <div className="flex flex-col gap-3 fade-in">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4 text-slate-400" />
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wide">Recent Usage</span>
                <span className="bg-brand-500 text-slate-900 px-2 py-0.5 text-[10px] font-bold rounded-full">{tasks.length}</span>
              </div>
              <Link
                to="/dashboard/search_history"
                className="text-sm font-bold text-brand-700 hover:text-brand-900 transition-colors"
              >
                View All →
              </Link>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              {/* Mobile card list */}
              <div className="sm:hidden divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="px-5 py-4 flex flex-col gap-2">
                      <div className="h-4 bg-slate-100 rounded w-2/3 animate-pulse" />
                      <div className="h-3 bg-slate-100 rounded w-1/2 animate-pulse" />
                    </div>
                  ))
                ) : tasks.length === 0 ? (
                  <div className="px-5 py-12 text-center">
                    <RocketLaunchIcon className="w-8 h-8 text-slate-400 mx-auto mb-3" />
                    <p className="text-sm font-bold text-slate-400">No usage yet</p>
                    <Link to="/dashboard/search" className="mt-2 inline-block text-sm font-bold text-brand-700 hover:underline">
                      Start your first search →
                    </Link>
                  </div>
                ) : (
                  tasks.map(task => (
                    <div key={task.id} className="px-5 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`w-7 h-7 rounded-xl flex items-center justify-center shrink-0 ${
                            task.type === 'bulk' ? 'bg-slate-100 text-slate-700' : 'bg-brand-50 text-slate-800'
                          }`}>
                            {task.type === 'bulk'
                              ? <FolderIcon className="w-3.5 h-3.5" />
                              : <Squares2X2Icon className="w-3.5 h-3.5" />
                            }
                          </div>
                          <Link
                            to={`/dashboard/search_history/${task.id}`}
                            className="text-[13px] font-bold text-slate-900 hover:text-brand-700 transition-colors truncate"
                          >
                            {task.company}
                          </Link>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-2">
                          <BoltIcon className="w-3 h-3 text-slate-700" />
                          <span className="text-[14px] font-extrabold text-slate-800">{(task.credits_used || 0).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 mt-2 pl-9">
                        <span className="text-[11px] text-slate-400">{formatDate(task.created_at)}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                          task.type === 'bulk' ? 'bg-slate-100 text-slate-700' : 'bg-brand-50 text-slate-800'
                        }`}>
                          {task.type}{task.depth ? ` ${DEPTH_LABELS[task.depth] ?? `×${task.depth}`}` : ''}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Desktop table */}
              <table className="hidden sm:table w-full text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-[9px] font-bold text-slate-400 uppercase tracking-wide">
                    <th className="px-6 py-3">Search</th>
                    <th className="px-6 py-3">Date</th>
                    <th className="px-6 py-3">Type</th>
                    <th className="px-6 py-3 text-right">Credits</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {[200, 100, 80, 60].map((w, j) => (
                          <td key={j} className="px-6 py-4">
                            <div className="h-3.5 bg-slate-100 rounded" style={{ width: w }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : tasks.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <RocketLaunchIcon className="w-8 h-8 text-slate-400 mx-auto mb-3" />
                        <p className="text-sm font-bold text-slate-400">No usage yet</p>
                        <Link to="/dashboard/search" className="mt-2 inline-block text-sm font-bold text-brand-700 hover:underline">
                          Start your first search →
                        </Link>
                      </td>
                    </tr>
                  ) : (
                    tasks.map(task => (
                      <tr key={task.id} className="group hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-7 h-7 rounded-xl flex items-center justify-center shrink-0 ${
                              task.type === 'bulk' ? 'bg-slate-100 text-slate-700' : 'bg-brand-50 text-slate-800'
                            }`}>
                              {task.type === 'bulk'
                                ? <FolderIcon className="w-3.5 h-3.5" />
                                : <Squares2X2Icon className="w-3.5 h-3.5" />
                              }
                            </div>
                            <Link
                              to={`/dashboard/search_history/${task.id}`}
                              className="text-[13px] font-bold text-slate-900 hover:text-brand-700 transition-colors"
                            >
                              {task.company}
                            </Link>
                          </div>
                        </td>
                        <td className="px-6 py-3.5 text-[12px] text-slate-500 font-medium whitespace-nowrap">{formatDate(task.created_at)}</td>
                        <td className="px-6 py-3.5">
                          <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${
                            task.type === 'bulk' ? 'bg-slate-100 text-slate-700' : 'bg-brand-50 text-slate-800'
                          }`}>{task.type}{task.depth ? ` ${DEPTH_LABELS[task.depth] ?? `×${task.depth}`}` : ''}</span>
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <BoltIcon className="w-3 h-3 text-slate-700" />
                            <span className="text-[14px] font-extrabold text-slate-800">{(task.credits_used || 0).toLocaleString()}</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Custom plans assigned by admin — purchasable only by this user */}
          {customPlans.length > 0 && (
            <div className="flex flex-col gap-3 fade-in">
              <div className="flex items-center gap-2">
                <BoltIcon className="w-4 h-4 text-emerald-500" />
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wide">Plans available for you</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {customPlans.map(plan => (
                  <div key={plan.id} className="bg-white border-2 border-emerald-300 rounded-2xl p-5 shadow-sm flex flex-col gap-3">
                    <div>
                      <p className="text-[11px] font-extrabold text-emerald-600 uppercase tracking-wider mb-1">Exclusive for you</p>
                      <p className="text-[18px] font-extrabold text-slate-900">{plan.name}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <BoltIcon className="w-4 h-4 text-brand-600" />
                      <span className="text-[22px] font-extrabold text-brand-700">{plan.credits.toLocaleString()}</span>
                      <span className="text-[12px] text-slate-400 font-semibold">credits</span>
                    </div>
                    <p className="text-[20px] font-extrabold text-slate-900">${Number(plan.price_monthly).toFixed(0)}</p>
                    <button
                      onClick={() => startCheckout({ id: plan.id } as PlanCard)}
                      disabled={buyingId === plan.id}
                      className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-colors"
                    >
                      {buyingId === plan.id ? 'Processing…' : 'Buy Now'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Purchase History */}
          <div className="flex flex-col gap-3 fade-in">
            <div className="flex items-center gap-2">
              <ReceiptRefundIcon className="w-4 h-4 text-slate-400" />
              <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wide">Purchase History</span>
              <span className="bg-brand-500 text-slate-900 px-2 py-0.5 text-[10px] font-bold rounded-full">{payments.length}</span>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              {/* Mobile card list */}
              <div className="sm:hidden divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="px-5 py-4 flex flex-col gap-2">
                      <div className="h-4 bg-slate-100 rounded w-1/2 animate-pulse" />
                      <div className="h-3 bg-slate-100 rounded w-2/3 animate-pulse" />
                    </div>
                  ))
                ) : payments.length === 0 ? (
                  <div className="px-5 py-12 text-center">
                    <ArrowDownTrayIcon className="w-8 h-8 text-slate-400 mx-auto mb-3" />
                    <p className="text-sm font-bold text-slate-400">No purchases yet</p>
                    <button
                      onClick={() => setIsPlansOpen(true)}
                      className="mt-2 text-sm font-bold text-brand-700 hover:underline"
                    >
                      View plans →
                    </button>
                  </div>
                ) : (
                  payments.map(payment => (
                    <div key={payment.id} className="px-5 py-4 flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-bold text-slate-900">{payment.plan_name}</span>
                        <span className="text-[13px] font-bold text-slate-900">${payment.amount}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <BoltIcon className="w-3 h-3 text-brand-600" />
                          <span className="text-[13px] font-extrabold text-brand-700">+{payment.credits.toLocaleString()}</span>
                        </div>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${
                          payment.status === 'completed'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {payment.status === 'completed' && <CheckCircleIcon className="w-3 h-3" />}
                          {payment.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-400">{formatDate(payment.created_at)}</span>
                        {payment.expires_at ? (
                          <span className={new Date(payment.expires_at) < new Date() ? 'text-red-500 font-bold' : 'text-slate-400'}>
                            Exp: {formatDate(payment.expires_at)}
                          </span>
                        ) : (
                          <span className="text-slate-400">No expiry</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Desktop table */}
              <table className="hidden sm:table w-full text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-[9px] font-bold text-slate-400 uppercase tracking-wide">
                    <th className="px-6 py-3">Plan</th>
                    <th className="px-6 py-3">Date</th>
                    <th className="px-6 py-3">Credits</th>
                    <th className="px-6 py-3">Amount</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Expires</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i}>
                        {[160, 100, 80, 60, 80, 100].map((w, j) => (
                          <td key={j} className="px-6 py-4">
                            <div className="h-3.5 bg-slate-100 rounded" style={{ width: w }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : payments.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center">
                        <ArrowDownTrayIcon className="w-8 h-8 text-slate-400 mx-auto mb-3" />
                        <p className="text-sm font-bold text-slate-400">No purchases yet</p>
                        <button
                          onClick={() => setIsPlansOpen(true)}
                          className="mt-2 text-sm font-bold text-brand-700 hover:underline"
                        >
                          View plans →
                        </button>
                      </td>
                    </tr>
                  ) : (
                    payments.map(payment => (
                      <tr key={payment.id} className="group hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-3.5">
                          <span className="text-[13px] font-bold text-slate-900">{payment.plan_name}</span>
                        </td>
                        <td className="px-6 py-3.5 text-[12px] text-slate-500 font-medium whitespace-nowrap">
                          {formatDate(payment.created_at)}
                        </td>
                        <td className="px-6 py-3.5">
                          <div className="flex items-center gap-1">
                            <BoltIcon className="w-3 h-3 text-brand-600" />
                            <span className="text-[13px] font-extrabold text-brand-700">+{payment.credits.toLocaleString()}</span>
                          </div>
                        </td>
                        <td className="px-6 py-3.5">
                          <span className="text-[13px] font-bold text-slate-900">${payment.amount}</span>
                        </td>
                        <td className="px-6 py-3.5">
                          <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${
                            payment.status === 'completed'
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            {payment.status === 'completed' && <CheckCircleIcon className="w-3 h-3" />}
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-[12px] font-medium whitespace-nowrap">
                          {payment.expires_at ? (
                            <span className={new Date(payment.expires_at) < new Date() ? 'text-red-500 font-bold' : 'text-slate-500'}>
                              {formatDate(payment.expires_at)}
                            </span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Plans */}
          <div className="flex flex-col gap-4 fade-in">
            <div className="flex items-center gap-2">
              <BoltIcon className="w-4 h-4 text-slate-400" />
              <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wide">Available Plans</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
              {cards.map((plan, i) => {
                const isHov = hoveredPlan === i;
                const isScale = plan.featured;

                return (
                  <div
                    key={plan.id}
                    className="relative flex flex-col rounded-2xl overflow-hidden cursor-default bg-white transition-all duration-300"
                    style={{
                      border: isScale ? '2px solid #22c55e' : isHov ? '2px solid #cbd5e1' : '2px solid #e2e8f0',
                      transform: isScale
                        ? isHov ? 'translateY(-10px) scale(1.02)' : 'translateY(-6px) scale(1.01)'
                        : isHov ? 'translateY(-4px)' : 'translateY(0)',
                      boxShadow: isScale
                        ? isHov
                          ? '0 32px 64px -12px rgba(34,197,94,0.25), 0 8px 24px rgba(0,0,0,0.12)'
                          : '0 20px 48px -8px rgba(34,197,94,0.18), 0 8px 24px rgba(0,0,0,0.1)'
                        : isHov ? '0 12px 32px -8px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.05)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                    }}
                    onMouseEnter={() => setHoveredPlan(i)}
                    onMouseLeave={() => setHoveredPlan(null)}
                  >
                    <div className="p-6 flex flex-col flex-1">

                      {/* name + badge */}
                      <div className="flex items-center justify-between mb-4">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${isScale ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {plan.name}
                        </span>
                        {isScale && (
                          <span className="text-[10px] font-black uppercase tracking-wide bg-emerald-500 text-white px-2.5 py-1 rounded-full">
                            Most popular
                          </span>
                        )}
                      </div>

                      {/* price */}
                      <div className="mb-1">
                        <span className="text-[3.25rem] font-black tracking-tighter leading-none text-slate-900">
                          {plan.price}
                        </span>
                      </div>

                      {/* credits + savings */}
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[13px] font-semibold text-slate-500">{plan.creditsLabel}</span>
                        {plan.savingsLabel && (
                          <span className={`text-[11px] font-black rounded-full px-2 py-0.5 ${
                            isScale ? 'text-emerald-700 bg-emerald-500/10' : 'text-brand-700 bg-brand-500/10'
                          }`}>
                            {plan.savingsLabel}
                          </span>
                        )}
                      </div>

                      {/* per credit */}
                      <p className="text-[12px] font-medium mb-5 text-slate-400">
                        <span className={`font-black ${isScale ? 'text-emerald-600' : 'text-slate-800'}`}>{plan.perCredit}</span> per credit
                      </p>

                      {/* divider */}
                      <div className="h-px mb-4 bg-slate-100" />

                      {/* live features */}
                      <ul className="flex flex-col gap-2 mb-4">
                        {plan.features.map((f, j) => (
                          <li key={j} className="flex items-start gap-2.5 text-[13px] font-medium">
                            <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-slate-700">{f}</span>
                          </li>
                        ))}
                      </ul>

                      {/* spacer */}
                      <div className="flex-1" />

                      {/* CTA — buy directly (PayU checkout) */}
                      <button
                        onClick={() => startCheckout(plan)}
                        disabled={buyingId !== null}
                        className={`w-full text-center font-black text-[13.5px] py-3.5 rounded-xl transition-all duration-200 active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed ${
                          isScale
                            ? 'bg-emerald-500 text-white hover:bg-emerald-400 shadow-lg shadow-emerald-500/20'
                            : 'bg-slate-100 border-2 border-slate-200 text-slate-900 hover:bg-slate-900 hover:text-white hover:border-slate-900'
                        }`}
                      >
                        {buyingId === plan.id ? 'Redirecting…' : 'Get started'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-[11.5px] text-slate-400 text-center leading-relaxed">
              A standard search costs 1 credit. Deep-search and enrichments are priced separately. Credits expire 60 days from purchase.
            </p>
          </div>

        </div>
      </div>

      <PlansModal
        isOpen={isPlansOpen}
        onClose={() => setIsPlansOpen(false)}
        onPurchaseSuccess={() => {}}
      />
    </div>
  );
}
